import { Component, input, output, signal, computed, effect, ChangeDetectionStrategy, inject, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { PharmacyOption } from '../../../../core/interfaces/api.interface';
import { ProfileService } from '../../../../core/services/profile.service';
import { RELOAD_PAGE } from '../../../../core/pharmacy-context';
import { NotificationService } from '../../../services/notification.service';
import { SsoService } from '../../../../core/services/sso.service';
import { ButtonComponent } from '../button/button';
import { formatLisbonTime } from '../../../utils/lisbon-time.util';

export interface HeaderConfig {
  showLogo?: boolean;
  showNavigation?: boolean;
  showUserMenu?: boolean;
  showSearch?: boolean;
  showNotifications?: boolean;
  variant?: 'default' | 'transparent' | 'glass';
}

export interface NavigationItem {
  label: string;
  path: string;
  /** Material Symbols name, shown above the label in the bottom bar. */
  icon?: string;
  active?: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly sso = inject(SsoService);
  private readonly profileService = inject(ProfileService);
  private readonly notify = inject(NotificationService);
  private readonly reloadPage = inject(RELOAD_PAGE);

  readonly impersonating = computed(() => this.authService.impersonating());
  readonly expiresAtTime = computed(() => { const imp = this.impersonating(); return imp ? formatLisbonTime(imp.expiresAt) : ''; });
  readonly endingImpersonation = signal(false);

  constructor() {
    // The API ends a "Ver como" session at expiresAt: reload then, so the banner and the data follow it (I5).
    effect((onCleanup) => {
      const imp = this.impersonating();
      const left = imp ? new Date(imp.expiresAt).getTime() - Date.now() : NaN;
      if (Number.isNaN(left)) return;
      // Past expiresAt by this clock but still open at the API (clock skew): look again in 30 s, never in a reload loop.
      const timer = setTimeout(() => this.reloadPage(), left <= 0 ? 30_000 : Math.min(left, 2 ** 31 - 1));
      onCleanup(() => clearTimeout(timer));
    });
  }

  /** Ends the "Ver como" session and reloads, so no screen keeps the other profile's data (I2). */
  endImpersonation(): void {
    if (this.endingImpersonation()) return;
    this.endingImpersonation.set(true);
    this.profileService.endImpersonation().subscribe({
      next: () => this.reloadPage(),
      error: () => {
        this.endingImpersonation.set(false);
        this.notify.showError('Não foi possível terminar a sessão.');
      },
    });
  }

  @ViewChild('userMenuContainer', { static: false }) userMenuContainer?: ElementRef;
  @ViewChild('bottomMenuContainer', { static: false }) bottomMenuContainer?: ElementRef;
  @ViewChild('bottomMenu', { static: false }) bottomMenu?: ElementRef;

  // Inputs
  config = input<HeaderConfig>({
    showLogo: true,
    showNavigation: true,
    showUserMenu: true,
    showSearch: false,
    showNotifications: false,
    variant: 'default'
  });

  navigationItems = input<NavigationItem[]>([]);

  // Outputs
  menuItemClicked = output<string>();
  userMenuAction = output<string>();

  // Signals
  /** The header's account menu. */
  readonly isUserMenuOpen = signal(false);
  /** The bottom bar's account menu: a menu of its own, so opening one never opens the other. */
  readonly isBarMenuOpen = signal(false);
  readonly isSearchOpen = signal(false);
  readonly currentRoute = signal('');

  // Computed values
  readonly isAuthenticated = computed(() => this.authService.isAuthenticated());
  readonly currentUser = computed(() => this.authService.user());
  readonly headerClass = computed(() => `header-${this.config()?.variant || 'default'}`);
  readonly profileName = computed(() => this.authService.profileName());
  /** The bottom bar's account button: the first letters of the first two words of the name. */
  readonly initials = computed(() =>
    (this.profileName() || this.currentUser()?.email || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('')
  );
  /**
   * "Aplicações": the portal's launcher in the same tab, when the group session may open another application
   * (login-farmacias change app-switcher). A password session has no claims and never shows it; the sign-in state
   * is read first so a password sign-in after a group session that ended without a sign-out does not either.
   */
  readonly switcherUrl = computed(() =>
    this.isAuthenticated() && this.authService.isSsoSession() ? this.sso.switcherUrl() : null
  );
  /** A row without an ANF cannot be put in context (it would send "null"), so it is not offered. */
  readonly pharmacies = computed(() => (this.authService.pharmacies() ?? []).filter((p) => !!p.anf)); // ?? [] for a stubbed AuthService in specs
  readonly currentAnf = computed(() => this.pharmacies().find((p) => p.current)?.anf ?? '');
  /** While "Ver como" the API serves the other profile, so the person's own pharmacies are not offered (M5). */
  readonly showPicker = computed(() => !this.impersonating() && this.pharmacies().length > 1);
  readonly canManageMembers = computed(() => this.authService.canManageMembers());

  pharmacyLabel(p: PharmacyOption): string { return p.name || `Farmácia ${p.anf}`; }

  /** Refused (no access there, an inactive account): the select goes back to the pharmacy in context (I4). */
  async onPharmacyChange(event: Event): Promise<void> {
    const select = event.target as HTMLSelectElement;
    try {
      await this.authService.selectPharmacy(select.value);
    } catch {
      select.value = this.currentAnf();
      this.notify.showError('Não foi possível mudar de farmácia.');
    }
  }

  readonly isAdmin = computed(() => this.authService.isAdmin());
  readonly hasMatAccess = computed(() => this.authService.hasMatAccess());
  /** Ezfy administrator signed in without a pharmacy profile: the admin pages are the only sections. */
  readonly adminOnly = computed(() => this.authService.ssoAdminOnly());

  // Default navigation items
  readonly defaultNavigationItems = computed<NavigationItem[]>(() => {
    const currentUrl = this.currentRoute();
    // A section is where its pages are: the list, a record in it, or the list with a query.
    const at = (path: string) => currentUrl === path || currentUrl.startsWith(`${path}/`) || currentUrl.startsWith(`${path}?`);
    const user = this.currentUser();
    const profileRole = user?.profile?.role?.toLowerCase();

    if (this.adminOnly()) {
      return [
        { label: 'Utilizadores registados', path: '/admin/users', icon: 'manage_accounts', active: at('/admin/users') },
        { label: 'Sincronização de farmácias', path: '/admin/farmacias-sync', icon: 'sync', active: at('/admin/farmacias-sync') },
        { label: 'Mensagens SMS', path: '/admin/sms', icon: 'sms', active: at('/admin/sms') }
      ];
    }

         // Hospital users see Tasks, Tracking, and Patients
         if (profileRole === 'hospital') {
           return [
             { label: 'Início', path: '/home', icon: 'home', active: at('/home') },
             { label: 'Tarefas', path: '/tasks', icon: 'task_alt', active: at('/tasks') },
             { label: 'Tracking', path: '/tracking', icon: 'local_shipping', active: at('/tracking') },
             { label: 'Utentes', path: '/patients', icon: 'groups', active: at('/patients') }
           ];
         }
    
    // Default navigation for other users
    const items: NavigationItem[] = [
      { label: 'Início', path: '/home', icon: 'home', active: at('/home') },
      { label: 'Dashboard', path: '/dashboard', icon: 'dashboard', active: at('/dashboard') },
      { label: 'Utentes', path: '/patients', icon: 'groups', active: at('/patients') },
    ];

    if (this.hasMatAccess()) {
      items.push({ label: 'MAT', path: '/mat', icon: 'medication', active: at('/mat') });
    }

    items.push(
      { label: 'Acompanhamentos', path: '/cases', icon: 'clinical_notes', active: at('/cases') },
      { label: 'Tarefas', path: '/tasks', icon: 'task_alt', active: at('/tasks') },
      { label: 'Recursos Ezfy', path: '/resources', icon: 'menu_book', active: at('/resources') }
    );

    return items;
  });

  readonly displayNavigationItems = computed(() => 
    this.navigationItems().length > 0 ? this.navigationItems() : this.defaultNavigationItems()
  );

  ngOnInit(): void {
    // The header appears once signed in, after the first navigation has ended: start from where we are.
    this.currentRoute.set(this.router.url);

    // Subscribe to router events to update current route
    this.router.events.subscribe(() => {
      this.currentRoute.set(this.router.url);
    });

    // Add click outside listener
    this.addClickOutsideListener();
  }

  ngOnDestroy(): void {
    this.removeClickOutsideListener();
  }

  private addClickOutsideListener(): void {
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  private removeClickOutsideListener(): void {
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  private onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // The menu opens from the header or from the bottom bar; a click inside either keeps it open.
    const inMenu = [this.userMenuContainer, this.bottomMenuContainer, this.bottomMenu]
      .some((c) => c?.nativeElement.contains(target));
    if (!inMenu && (this.isUserMenuOpen() || this.isBarMenuOpen())) {
      this.isUserMenuOpen.set(false);
      this.isBarMenuOpen.set(false);
    }
  }

  // Methods
  toggleUserMenu(): void {
    this.isBarMenuOpen.set(false);
    this.isUserMenuOpen.update(open => !open);
  }

  toggleBarMenu(): void {
    this.isUserMenuOpen.set(false);
    this.isBarMenuOpen.update(open => !open);
  }

  toggleSearch(): void {
    this.isSearchOpen.update(open => !open);
  }

  onNavigationClick(item: NavigationItem): void {
    if (!item.disabled) {
      this.menuItemClicked.emit(item.path);
      this.router.navigate([item.path]);
    }
  }

  onLogoClick(): void {
    // Navigate based on authentication and role
    if (this.isAuthenticated()) {
      const user = this.currentUser();
      const profileRole = user?.profile?.role?.toLowerCase();

      if (this.adminOnly()) {
        this.router.navigate(['/admin/users']);
      } else if (profileRole === 'hospital') {
        this.router.navigate(['/tasks']);
      } else {
        this.router.navigate(['/dashboard']);
      }
    } else {
      this.router.navigate(['/']);
    }
  }

  onUserMenuAction(action: string): void {
    this.userMenuAction.emit(action);
    this.isUserMenuOpen.set(false);
    this.isBarMenuOpen.set(false);
    
    switch (action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'team':
        this.router.navigate(['/team']);
        break;
      case 'pharmacy-accounts':
        this.router.navigate(['/pharmacy/accounts']);
        break;
      case 'admin-users':
        this.router.navigate(['/admin/users']);
        break;
      case 'farmacia-sync':
        this.router.navigate(['/admin/farmacias-sync']);
        break;
      case 'sms-usage':
        this.router.navigate(['/admin/sms']);
        break;
      case 'logout': {
        // A group session ends at the portal, which navigates away from here itself; routing
        // home as well would show this app for the moment that takes. Asked before the
        // sign-out, because it clears the mark that answers it.
        const group = this.authService.isSsoSession();
        this.authService.logout().then(() => {
          if (!group) {
            this.router.navigate(['/']);
          }
        });
        break;
      }
    }
  }

  closeMenus(): void {
    this.isUserMenuOpen.set(false);
    this.isBarMenuOpen.set(false);
    this.isSearchOpen.set(false);
  }
}
